import { IExtensionInfo, IFlagsConfig, IUser } from '@/models';
import { versionUtils } from './version';
import {
  OPT_IN_PREFERENCE,
  OptInPreference,
} from '@/constants/features/opt-in';
import { ONE_WEEK_IN_MILLISECONDS } from '@/constants/app/times';
import { isUserAnonymous } from './user';

type OptInFlags = Pick<IFlagsConfig, 'optInPreferences' | 'isPrivacyAgreed'>;

// New schema -> array membership; legacy -> only clickStream (isPrivacyAgreed), others false.
export const isOptedIn = (
  flags: OptInFlags,
  preference: OptInPreference
): boolean => {
  if (Array.isArray(flags.optInPreferences)) {
    return flags.optInPreferences.includes(preference);
  }
  return preference === OPT_IN_PREFERENCE.CLICK_STREAM
    ? flags.isPrivacyAgreed
    : false;
};

export const isClickStreamOptedIn = (flags: OptInFlags): boolean =>
  isOptedIn(flags, OPT_IN_PREFERENCE.CLICK_STREAM);

// Opted into anything beyond Browsing Data (any non-cs id).
export const isExtendedDataOptedIn = (
  flags: Pick<IFlagsConfig, 'optInPreferences'>
): boolean =>
  Array.isArray(flags.optInPreferences) &&
  flags.optInPreferences.some(
    preference => preference !== OPT_IN_PREFERENCE.CLICK_STREAM
  );

// On the new schema iff the explicit flag was set (migration, install, onboarding consent).
export const isOnBucketsSchema = (
  flags: Pick<IFlagsConfig, 'onNewOptInSchema'>
): boolean => flags.onNewOptInSchema === true;

export const isExtensionOnBucketsOptInVersion = (
  extension?: Pick<IExtensionInfo, 'isInstalled' | 'extensionVersion'> | null
): boolean => {
  if (!extension?.isInstalled) {
    return true;
  }
  return versionUtils.supportsBucketsOptIn(extension.extensionVersion);
};

export const canUseBucketsOptInExperience = (
  flags: Pick<IFlagsConfig, 'onNewOptInSchema'>,
  extension?: Pick<IExtensionInfo, 'isInstalled' | 'extensionVersion'> | null
): boolean =>
  isOnBucketsSchema(flags) && isExtensionOnBucketsOptInVersion(extension);

export const hasSeenBucketsOptInScreen = (
  flags: Pick<IFlagsConfig, 'sawBucketsOptInOnboardingScreen'>
): boolean => !!flags.sawBucketsOptInOnboardingScreen;

type ReconsentFlags = Pick<
  IFlagsConfig,
  | 'onNewOptInSchema'
  | 'sawBucketsOptInOnboardingScreen'
  | 'optInPreferences'
  | 'reconsentBannerDismissed'
>;

const isInstalledAtLeastOneWeek = (installedTime: number): boolean =>
  installedTime > 0 && Date.now() - installedTime >= ONE_WEEK_IN_MILLISECONDS;

export const shouldShowTermsUpdatedModal = (
  flags: IFlagsConfig,
  user: IUser,
  isOnboarding = false,
  installedTime = 0
): boolean =>
  !isOnboarding &&
  !flags.eulaAgreed &&
  flags.onNewOptInSchema === true &&
  isUserAnonymous(user) &&
  isInstalledAtLeastOneWeek(installedTime);

// Banner shows while on the new schema, not opted in beyond Browsing Data, not dismissed.
export const shouldShowReconsentBanner = (
  flags: ReconsentFlags,
  bannerFlagEnabled: boolean,
  isOnboardingOpen = false
): boolean =>
  bannerFlagEnabled &&
  !isOnboardingOpen &&
  isOnBucketsSchema(flags) &&
  !isExtendedDataOptedIn(flags) &&
  !flags.reconsentBannerDismissed;

// No dismiss X until the user has opened the modal at least once.
export const canDismissReconsentBanner = (
  flags: Pick<IFlagsConfig, 'sawBucketsOptInOnboardingScreen'>
): boolean => hasSeenBucketsOptInScreen(flags);

export const applyPreference = (
  optInPreferences: OptInPreference[],
  preference: OptInPreference,
  optedIn: boolean
): OptInPreference[] => {
  const preferenceSet = new Set(optInPreferences);
  if (optedIn) {
    preferenceSet.add(preference);
  } else {
    preferenceSet.delete(preference);
  }
  return Array.from(preferenceSet);
};
